// Pure helpers for the Event Ops page header (follow-up to SCRUM-429/449):
// collapsing the three separate date lines into one chronological line, and
// the five-stage pipeline strip into a single pill. Kept out of
// EventOps.jsx, which can't be imported in Jest (it renders through
// react-router hooks outside a router) — these are plain functions the
// component feeds its own computed values into.

const CT = 'America/Chicago';

const weekday = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: CT, weekday: 'short' });
const monthDay = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: CT, month: 'short', day: 'numeric' });
const year = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: CT, year: 'numeric' });
const time = (iso) => new Date(iso).toLocaleString('en-US', { timeZone: CT, hour: 'numeric', minute: '2-digit' });

// "Thu 8:00 AM" — events are short-lived, so the weekday is enough of a date;
// dropping month/day/year keeps the line to one glance.
const shortLabel = (iso) => `${weekday(iso)} ${time(iso)}`;

// "Thu Sep 24, 2026, 8:00 AM CT" — the full instant, for a tooltip so the
// trimmed display never actually loses information.
const fullTitle = (iso) => `${weekday(iso)} ${monthDay(iso)}, ${year(iso)}, ${time(iso)} CT`;

const sameCTDate = (a, b) =>
  new Date(a).toLocaleDateString('en-US', { timeZone: CT }) ===
  new Date(b).toLocaleDateString('en-US', { timeZone: CT });

// One chronological line, always in Central (operators are in Texas
// whatever the event's own zone — same reasoning as campaign/time.js's
// fmtCT): "Registration <time> → Event <start> – <end> → Sales end <time>".
// Returns an array of parts rather than a string because each timestamp
// needs its own full-date tooltip (EventOps renders `type: 'time'` parts as
// `<span title>`, `type: 'text'` parts as plain text). Any leg whose
// timestamp is missing is dropped and the arrows re-join around the gap.
// The event's end repeats only the time when it falls on the same Central
// calendar day as the start; otherwise it gets its own weekday too.
export function formatEventScheduleParts({ registrationStartsAt, startsAt, endsAt, expiresAt } = {}) {
  const parts = [];
  const text = (t) => parts.push({ type: 'text', text: t });
  const chip = (iso, label) => parts.push({ type: 'time', text: label, title: fullTitle(iso) });

  if (registrationStartsAt) {
    text('Registration ');
    chip(registrationStartsAt, shortLabel(registrationStartsAt));
  }
  if (startsAt) {
    text(parts.length ? ' → Event ' : 'Event ');
    chip(startsAt, shortLabel(startsAt));
    if (endsAt) {
      text(' – ');
      chip(endsAt, sameCTDate(startsAt, endsAt) ? time(endsAt) : shortLabel(endsAt));
    }
  }
  if (expiresAt) {
    text(parts.length ? ' → Sales end ' : 'Sales end ');
    chip(expiresAt, shortLabel(expiresAt));
  }
  return parts;
}

// Collapses the pipeline strip's five stage states into one pill. `stages`
// is an ordered array — [{ name, state, detail }] in the tie-break order
// EventOps builds it (Landing, Indexing, Rendering, Matching, Delivery) —
// `state` one of 'ok' | 'warn' | 'bad' | 'idle' and `detail` the same key
// number already shown in the detailed strip for that stage (e.g. "3
// waiting", "12 pending"). Idle doesn't count against the pill — a stage
// with nothing to do isn't behind — only warn/bad do, and bad always
// outranks warn; ties go to whichever stage is earlier in the array.
export function summarizePipeline(stages = []) {
  const bad = stages.find((s) => s.state === 'bad');
  if (bad) return { ok: false, level: 'bad', text: `${bad.name} is behind — ${bad.detail}` };
  const warn = stages.find((s) => s.state === 'warn');
  if (warn) return { ok: false, level: 'warn', text: `${warn.name} is behind — ${warn.detail}` };
  return { ok: true, level: 'ok', text: 'Photos pipeline: all good' };
}
